import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Scissors, Plus, Trash2, Loader2, AlertCircle, Download, X, Film, CheckCircle2 } from 'lucide-react';
import api, { getImageUrl } from '@/lib/api';
import { toast } from 'sonner';

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;

const ClipPicker = ({ open, onClose, onPick }) => {
  const [clips, setClips] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => { if (open) api.get('/workspace/scene-clips').then(r => setClips(r.data.clips)).catch(() => setClips([])); }, [open]);
  if (!open) return null;
  const filtered = (clips || []).filter(c => !q || `${c.project_name} ${c.title}`.toLowerCase().includes(q.toLowerCase()));
  const groups = filtered.reduce((acc, c) => { (acc[c.project_name] ||= []).push(c); return acc; }, {});
  return createPortal(
    <div className="fixed inset-0 z-[100] flex" data-testid="clip-picker">
      <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-xl bg-[#0d0d11] border-l border-white/10 h-full flex flex-col">
        <div className="p-4 border-b border-white/[0.06] flex items-center gap-3">
          <Film className="w-4 h-4 text-[#E879F9]" />
          <h3 className="text-sm font-semibold text-white flex-1">Pick a scene clip for B-roll</h3>
          <button onClick={onClose} data-testid="clip-picker-close" className="w-8 h-8 rounded-lg hover:bg-white/[0.06] text-[#8A8A93] flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 border-b border-white/[0.06]">
          <input data-testid="clip-picker-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search projects or scenes…" className="ff-input w-full rounded-lg px-3 py-2 text-sm" />
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-4">
          {clips === null ? <p className="text-xs text-[#8A8A93] flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading clips…</p>
            : filtered.length === 0 ? <p className="text-xs text-[#8A8A93]">No rendered scene videos in this workspace yet. Render a scene in a project first.</p>
            : Object.entries(groups).map(([proj, list]) => (
              <div key={proj}>
                <p className="text-[10px] uppercase tracking-wider text-[#8A8A93] mb-2">{proj || 'Project'}</p>
                <div className="grid grid-cols-2 gap-2">
                  {list.map(c => (
                    <button key={c.scene_id} data-testid={`clip-option-${c.scene_id}`} onClick={() => { onPick(c); onClose(); }}
                      className="text-left rounded-xl overflow-hidden border border-white/[0.08] hover:border-[#E879F9]/50 transition-colors bg-[#121216]">
                      <div className="aspect-video bg-black/40">
                        {c.thumb_path ? <img src={getImageUrl(c.thumb_path)} alt="" className="w-full h-full object-cover" /> : <video src={getImageUrl(c.video_path)} muted className="w-full h-full object-cover" />}
                      </div>
                      <div className="p-2 flex items-center gap-1.5">
                        <span className="text-xs text-white truncate flex-1">{c.title}</span>
                        {c.approved && <CheckCircle2 className="w-3 h-3 text-[#10B981] shrink-0" title="Approved take" />}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>, document.body);
};

export const PresenterCutawayPanel = ({ video, onRefresh }) => {
  const total = video.audio_duration || 0;
  const [cuts, setCuts] = useState(video.cutaways || []);
  const [picker, setPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);
  useEffect(() => { setCuts(video.cutaways || []); }, [video.cutaways]);

  const persist = (next) => {
    setCuts(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSaving(true);
      try { await api.put(`/presenter/${video.id}/cutaways`, next); await onRefresh(); }
      catch (e) { toast.error(e.response?.data?.detail || 'Could not save timeline'); await onRefresh(); }
      finally { setSaving(false); }
    }, 600);
  };

  const add = (clip) => {
    // First gap of ≥1s on the timeline, scanning left to right.
    let cursor = 0, start = null;
    for (const c of sorted) {
      if (c.start - cursor >= 1) { start = cursor; break; }
      cursor = Math.max(cursor, c.start + c.duration);
    }
    if (start === null) {
      if (total - cursor < 1) { toast.error('No room left on the timeline — shorten or remove a cutaway first'); return; }
      start = cursor;
    }
    const gapEnd = sorted.find(c => c.start >= start + 0.5)?.start ?? total;
    const duration = Math.max(1, Math.min(4, gapEnd - start));
    persist([...cuts, { id: `tmp-${Date.now()}`, scene_id: clip.scene_id, project_id: clip.project_id, video_path: clip.video_path, thumb_path: clip.thumb_path, label: `${clip.project_name} · ${clip.title}`, start: Math.round(start * 2) / 2, duration: Math.round(duration * 2) / 2, clip_offset: 0 }]);
  };
  const edit = (i, patch) => persist(cuts.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const remove = (i) => persist(cuts.filter((_, idx) => idx !== i));

  const mix = async () => {
    try { await api.post(`/presenter/${video.id}/mix`); await onRefresh(); toast.success('Mixing your edit…'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not start mix'); }
  };

  const mixing = video.mix_status === 'mixing';
  const stale = video.mixed_video_path && video.mixed_from_video && video.mixed_from_video !== video.video_path;
  const sorted = useMemo(() => [...cuts].sort((a, b) => a.start - b.start), [cuts]);
  if (!video.video_path) return null;

  return (
    <section className="ff-card rounded-2xl p-5" data-testid="presenter-cutaway-panel">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#E879F9] flex items-center gap-2"><Scissors className="w-3.5 h-3.5" /> 5 · B-roll cutaways</h2>
        <span className="text-[10px] text-[#8A8A93]">{saving ? 'Saving…' : `${cuts.length} cutaway${cuts.length === 1 ? '' : 's'} · presenter voice runs throughout`}</span>
      </div>

      <div className="relative h-10 rounded-lg bg-[#121216] border border-white/[0.06] overflow-hidden mb-3" data-testid="cutaway-timeline">
        <div className="absolute inset-0 flex items-center px-2 text-[10px] text-[#55555C] font-mono">0:00<span className="ml-auto">{fmt(total)}</span></div>
        {sorted.map((c, i) => (
          <div key={c.id || i} data-testid={`cutaway-block-${i}`} title={c.label}
            className="absolute top-1 bottom-1 rounded-md bg-[#E879F9]/30 border border-[#E879F9]/60 overflow-hidden"
            style={{ left: `${(c.start / Math.max(total, 1)) * 100}%`, width: `${(c.duration / Math.max(total, 1)) * 100}%` }}>
            {c.thumb_path && <img src={getImageUrl(c.thumb_path)} alt="" className="w-full h-full object-cover opacity-60" />}
          </div>
        ))}
      </div>

      <div className="space-y-2">
        {cuts.map((c, i) => (
          <div key={c.id || i} data-testid={`cutaway-row-${i}`} className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.02] border border-white/[0.06]">
            <div className="w-14 h-9 rounded-md bg-black/40 overflow-hidden shrink-0">{c.thumb_path && <img src={getImageUrl(c.thumb_path)} alt="" className="w-full h-full object-cover" />}</div>
            <span className="text-xs text-white truncate flex-1 min-w-0">{c.label}</span>
            {[['start', 'at', 0, Math.max(0, total - 0.5)], ['duration', 'for', 0.5, total], ['clip_offset', 'clip from', 0, 600]].map(([k, lbl, min, max]) => (
              <label key={k} className="flex items-center gap-1 text-[10px] text-[#8A8A93]">
                {lbl}
                <input data-testid={`cutaway-${k}-${i}`} type="number" step="0.5" min={min} max={max} value={c[k]} onChange={e => edit(i, { [k]: Math.max(min, Math.min(max, parseFloat(e.target.value) || 0)) })}
                  className="ff-input w-16 rounded-md px-1.5 py-1 text-[11px] font-mono text-right" />s
              </label>
            ))}
            <button data-testid={`cutaway-remove-${i}`} onClick={() => remove(i)} className="w-7 h-7 rounded-md text-[#8A8A93] hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3">
        <button data-testid="cutaway-add-btn" onClick={() => setPicker(true)} className="ff-btn-secondary text-xs flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add scene clip</button>
        <button data-testid="cutaway-mix-btn" onClick={mix} disabled={!cuts.length || mixing || saving} className="ff-btn-primary text-xs flex items-center gap-2 disabled:opacity-50 ml-auto">
          {mixing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Scissors className="w-3.5 h-3.5" />} {video.mixed_video_path ? 'Re-mix edit' : 'Create mixed edit'}
        </button>
      </div>
      {mixing && <p className="text-[11px] text-[#E879F9] mt-2 flex items-center gap-1.5" data-testid="mix-progress"><Loader2 className="w-3 h-3 animate-spin" /> {video.mix_progress || 'Mixing…'}</p>}
      {video.mix_status === 'failed' && <p data-testid="mix-error" className="text-[11px] text-red-400 mt-2 flex items-start gap-1.5"><AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" /> {video.mix_error}</p>}
      {stale && <p className="text-[11px] text-amber-400 mt-2">The presenter video was re-rendered since this mix — re-mix to update.</p>}
      {video.mixed_video_path && (
        <div className="mt-4" data-testid="mix-result">
          <video controls src={getImageUrl(video.mixed_video_path)} className="w-full rounded-xl bg-black max-h-[420px]" data-testid="mix-video" />
          <div className="flex justify-end mt-2">
            <a data-testid="mix-download" href={getImageUrl(video.mixed_video_path)} download={`${video.title || 'presenter'}-mixed.mp4`} className="ff-btn-secondary text-xs flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> Download mixed MP4</a>
          </div>
        </div>
      )}
      <ClipPicker open={picker} onClose={() => setPicker(false)} onPick={add} />
    </section>
  );
};
